
import React from 'react';
import { Node } from '@xyflow/react';
import { NodeData } from './types';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X, Trash2 } from 'lucide-react';

interface NodeConfigPanelProps {
  node: Node<NodeData>;
  onChange: (nodeId: string, data: Partial<NodeData>) => void;
  onClose: () => void;
  onDelete: (nodeId: string) => void;
}

const NodeConfigPanel: React.FC<NodeConfigPanelProps> = ({ node, onChange, onClose, onDelete }) => {
  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(node.id, { label: e.target.value });
  };

  const handleTypeChange = (type: string) => {
    onChange(node.id, { type });
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this node?')) {
      onDelete(node.id);
    }
  };

  // Safely access node data
  const nodeData = node.data || {};
  const nodeType = nodeData.type as string || 'capability';
  const nodeConfig = nodeData.config as Record<string, unknown> || {};

  return (
    <Card className="w-80 border-l-0 rounded-l-none h-full">
      <CardHeader className="flex flex-row items-center justify-between py-3">
        <CardTitle className="text-base">Node Configuration</CardTitle>
        <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="node-label">Label</Label>
          <Input
            id="node-label"
            value={nodeData.label as string || ''}
            onChange={handleLabelChange}
            placeholder="Enter node label"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="node-type">Type</Label>
          <Select
            value={nodeType}
            onValueChange={handleTypeChange}
          >
            <SelectTrigger id="node-type">
              <SelectValue placeholder="Select node type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="capability">Capability</SelectItem>
              <SelectItem value="condition">Condition</SelectItem>
              <SelectItem value="input">Input</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {nodeType === 'capability' && (
          <div className="space-y-2">
            <Label htmlFor="capability-name">Capability</Label>
            <Input
              id="capability-name"
              value={(nodeConfig?.capability_name as string) || ''}
              onChange={(e) => {
                const updatedConfig = { 
                  ...nodeConfig, 
                  capability_name: e.target.value 
                };
                onChange(node.id, { 
                  config: updatedConfig
                });
              }}
              placeholder="Enter capability name"
            />
          </div>
        )}
        
        {nodeType === 'condition' && (
          <div className="space-y-2">
            <Label htmlFor="condition">Condition</Label>
            <Input
              id="condition"
              value={(nodeConfig?.condition as string) || ''}
              onChange={(e) => {
                const updatedConfig = { 
                  ...nodeConfig, 
                  condition: e.target.value 
                };
                onChange(node.id, { 
                  config: updatedConfig
                });
              }}
              placeholder="Enter condition"
            />
          </div>
        )}
        
        {nodeType === 'input' && (
          <div className="space-y-2">
            <Label htmlFor="variable-name">Variable Name</Label>
            <Input
              id="variable-name"
              value={(nodeConfig?.variable_name as string) || ''}
              onChange={(e) => {
                const updatedConfig = { 
                  ...nodeConfig, 
                  variable_name: e.target.value 
                };
                onChange(node.id, { 
                  config: updatedConfig
                });
              }}
              placeholder="Enter variable name"
            />
          </div>
        )}
      </CardContent>
      
      <CardFooter className="flex justify-between">
        <Button
          variant="destructive"
          size="sm"
          onClick={handleDelete}
          className="flex items-center gap-1"
        >
          <Trash2 className="h-4 w-4" />
          Delete
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onClose}
        >
          Done
        </Button>
      </CardFooter>
    </Card>
  );
};

export default NodeConfigPanel;
